import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@h2/ui';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@h2/ui';
import { Input } from '@h2/ui';
import { Textarea } from '@h2/ui';
import { Button } from '@h2/ui';
import { Checkbox } from '@h2/ui';
import { Label } from '@h2/ui';
import { useTaskTypeStore } from '../../stores/useTaskTypeStore';

const taskTypeSchema = z.object({
  name: z.string().min(1, '이름을 입력해주세요').max(50, '이름은 50자 이내로 입력해주세요'),
  memo: z.string().max(200, '설명은 200자 이내로 입력해주세요'),
  icon: z.string().min(1, '아이콘을 선택해주세요'),
  languageDependent: z.boolean(),
  order: z.number().min(1, '순서는 1 이상이어야 합니다').max(100, '순서는 100 이하여야 합니다'),
});

type TaskTypeFormData = z.infer<typeof taskTypeSchema>;

interface TaskType {
  id: string;
  name: string;
  memo: string;
  languageDependent: boolean;
  icon: string;
  order: number;
}

interface TaskTypeFormModalProps {
  taskType?: TaskType;
  onSave: () => void;
  onCancel: () => void;
}

const AVAILABLE_ICONS = [
  { emoji: '📝', name: '전사' },
  { emoji: '🔍', name: '검수' },
  { emoji: '🌐', name: '번역' },
  { emoji: '✅', name: '확인' },
  { emoji: '👤', name: 'PM' },
  { emoji: '🎬', name: '렌더링' },
  { emoji: '📋', name: '기획' },
  { emoji: '🎨', name: '디자인' },
  { emoji: '⚙️', name: '설정' },
  { emoji: '📊', name: '분석' },
];

export const TaskTypeFormModal: React.FC<TaskTypeFormModalProps> = ({
  taskType,
  onSave,
  onCancel
}) => {
  const { addTaskType, updateTaskType } = useTaskTypeStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<TaskTypeFormData>({
    resolver: zodResolver(taskTypeSchema),
    defaultValues: {
      name: taskType?.name || '',
      memo: taskType?.memo || '',
      icon: taskType?.icon || '📝',
      languageDependent: taskType?.languageDependent || false,
      order: taskType?.order || 1,
    },
  });

  const handleSubmit = async (data: TaskTypeFormData) => {
    setIsSubmitting(true);
    try {
      if (taskType) {
        // 수정
        await updateTaskType(taskType.id, data);
      } else {
        // 생성
        addTaskType(data);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save task type:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {taskType ? '작업유형 편집' : '새 작업유형 생성'}
          </DialogTitle>
          <DialogDescription>
            워크플로우에서 사용할 작업 유형을 {taskType ? '수정' : '생성'}합니다.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* 기본 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">기본 정보</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>이름</FormLabel>
                      <FormControl>
                        <Input placeholder="예: 번역" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="order"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>순서</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="1" 
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                        />
                      </FormControl>
                      <FormDescription>워크플로우에서의 표시 순서</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="memo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>설명</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="작업유형에 대한 설명을 입력하세요"
                        className="h-20"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* 아이콘 선택 */}
            <div className="space-y-3">
              <Label>아이콘</Label>
              <FormField
                control={form.control}
                name="icon"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="grid grid-cols-5 gap-2">
                        {AVAILABLE_ICONS.map((iconItem) => (
                          <button
                            key={iconItem.emoji}
                            type="button"
                            className={`p-3 border rounded-lg hover:bg-accent transition-colors ${
                              field.value === iconItem.emoji 
                                ? 'border-primary bg-primary/10' 
                                : 'border-border'
                            }`}
                            onClick={() => field.onChange(iconItem.emoji)}
                          >
                            <div className="text-center">
                              <div className="text-2xl mb-1">{iconItem.emoji}</div>
                              <div className="text-xs text-muted-foreground">{iconItem.name}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* 속성 설정 */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">속성 설정</h3>
              
              <FormField
                control={form.control}
                name="languageDependent"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>다국어 작업</FormLabel>
                      <FormDescription>
                        이 작업이 언어별로 개별적으로 수행되어야 하는 경우 체크하세요
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onCancel}>
                취소
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? '저장 중...' : (taskType ? '수정' : '생성')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
